import {
	Children,
	cloneElement,
	type ComponentType,
	createContext,
	type FC,
	type PropsWithChildren,
	type ReactElement,
	useContext,
	useMemo,
} from 'react';

import { type Country, type Repub } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import { type SiteConfig } from '~/api/site-config-api/types';
import {
	CookieService,
	type ICookieService,
} from '~/services/actions/cookie-actions';
// import ErrorActions, {
//   type IErrorActions,
// } from "~/services/actions/error-actions";
import LabelService, {
	type ILabelActions,
} from '~/services/actions/labels-actions';
import LinkActions, {
	type ILinkActions,
} from '~/services/actions/link-actions';
import SiteConfigActions, {
	type ISiteConfigActions,
} from '~/services/actions/site-config-actions';
import TrackingActions, {
	type ITrackingActions,
} from '~/services/actions/tracking-actions';

export type ServicesProviderProps = {
	NODE_ENV: 'development' | 'production' | 'test';
	countryCode: Country;
	labels: Record<string, string>;
	repub: Repub;
	siteConfig: SiteConfig;
	state: StateCode | '';
};

export type ServicesProviderData = {
	DEV_ENV: boolean;
	NODE_ENV: 'production' | 'development' | 'test';
	cookieActions: ICookieService;
	countryCode: Country;
	// errorActions: IErrorActions;
	labelActions: ILabelActions;
	linkActions: ILinkActions;
	repub: Repub;
	siteConfig: SiteConfig;
	siteConfigActions: ISiteConfigActions;
	state: StateCode | '';
	trackingActions: ITrackingActions;
};

// Context
const ServicesContext = createContext<ServicesProviderData>(
	undefined as any as ServicesProviderData,
);
ServicesContext.displayName = 'ServicesContext';

// Provider
const ServicesProvider: FC<PropsWithChildren<ServicesProviderProps>> = ({
	NODE_ENV,
	children,
	countryCode,
	labels,
	repub,
	siteConfig,
	state,
	...props
}) => {
	const DEV_ENV = NODE_ENV !== 'production';

	const cookieActions = useMemo(() => new CookieService(), []);
	// const errorActions = useMemo(() => new ErrorActions(), []);
	const labelActions = useMemo(
		() => new LabelService(DEV_ENV, labels),
		[DEV_ENV, labels],
	);
	const linkActions = useMemo(() => new LinkActions(), []);
	const trackingActions = useMemo(() => new TrackingActions(), []);
	const siteConfigActions = useMemo(() => new SiteConfigActions(true), []);

	const value = useMemo(
		() => ({
			DEV_ENV,
			NODE_ENV,
			// errorActions,
			siteConfig,
			cookieActions,
			labelActions,
			linkActions,
			siteConfigActions,
			trackingActions,
			labels,
			repub,
			countryCode,
			state,
		}),
		[
			DEV_ENV,
			NODE_ENV,
			siteConfig,
			cookieActions,
			labelActions,
			linkActions,
			siteConfigActions,
			trackingActions,
			labels,
			countryCode,
			state,
			repub,
		],
	);
	return (
		<ServicesContext.Provider value={value} {...props}>
			{children}
		</ServicesContext.Provider>
	);
};

const useServicesContext = () => {
	const servicesContext = useContext(ServicesContext);
	if (!servicesContext) {
		throw new Error(
			'useServicesContext can only be used inside of ServicesProvider',
		);
	}

	return servicesContext;
};

// NOTE: this Compond Component pattern is great if there
// are multiple siblings, but this is unlikely
const withServicesContextCC = ({ children }: PropsWithChildren) => {
	return (
		<ServicesContext.Consumer>
			{servicesContext => {
				return Children.map(children, child => {
					const item = child as ReactElement;

					// Return item if regular DOM element
					if (typeof item.type === 'string') {
						return item;
					}

					// Return compound component
					return cloneElement(item, {
						context: servicesContext,
					});
				});
			}}
		</ServicesContext.Consumer>
	);
};

// NOTE: this HOC pattern is perfect for single children
function withServicesContextHOC<T extends ServicesProviderData>(
	Component: ComponentType<T>,
) {
	// NOTE: Render Props and Hooks implementations are interchangeable here,
	// with both examples included below for documentation purposes:

	// Option #1 - Render Props
	const ComponentWithServicesContext = (
		props: Omit<T, keyof ServicesProviderData>,
	) => (
		<ServicesContext.Consumer>
			{servicesContext => (
				<Component {...(props as any as T)} context={servicesContext} />
			)}
		</ServicesContext.Consumer>
	);

	// Option #2 - Hook
	// const ComponentWithServicesContext = (
	//   props: Omit<T, keyof ServicesProviderData>
	// ) => {
	//   const servicesContext = useServicesContext();

	//   return <Component {...(props as T)} context={servicesContext} />;
	// };

	// Ensure we assign a display name to support with debugging
	const displayName = Component.displayName || Component.name || 'Component';
	ComponentWithServicesContext.displayName = `withServicesContextHOC(${displayName})`;

	return ComponentWithServicesContext;
}

const ServicesProviderWrapper: FC<PropsWithChildren<ServicesProviderProps>> = ({
	NODE_ENV,
	children,
	countryCode,
	labels,
	repub,
	siteConfig,
	state,
}) => {
	return (
		<ServicesProvider
			NODE_ENV={NODE_ENV}
			countryCode={countryCode}
			labels={labels}
			repub={repub}
			siteConfig={siteConfig}
			state={state}
		>
			{children}
		</ServicesProvider>
	);
};

export {
	ServicesContext,
	useServicesContext,
	withServicesContextCC,
	withServicesContextHOC,
	ServicesProviderWrapper as ServicesProvider,
};
